import { ActionIcon } from "@mantine/core";
import { IconChevronLeft, IconChevronRight } from "@tabler/icons-react";
import classNames from "classnames";
import dayjs from "dayjs";
import type { CSSProperties } from "react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "src/redux/hooks";
import { setEntryDate } from "src/redux/slices/generatorSlice";

import {
  markEditing,
  usePanelNavigationStore,
  useScopedGeneratorStore,
} from "../../../store";
import type {
  V2ContentNode,
  V2Group,
  V2Item,
  V2LabelDef,
} from "../../../types";
import { isV2Group } from "../../../types";
import { resolveGroupFlex } from "../../../utils/styleUtils";
import { Button } from "../../inputs/Button/Button";
import { DateInput } from "../../inputs/DateInput/DateInput";
import { Item } from "../../Item";
import styles from "./Group.module.scss";

interface GroupLabelProps {
  label: V2LabelDef;
  entrySelector?: {
    id: string;
    value: string;
    onChange: (value: string) => void;
  };
}

// ─── Group Header Label ───────────────────────────────────────────────────────

function GroupLabel({ label, entrySelector }: GroupLabelProps) {
  const {
    text,
    panelText: _panelText,
    description,
    textAlign,
    color,
    backColor,
    outline,
    padding,
    bold,
    italic,
    underline,
    style: labelStyle,
    orientation: _orientation,
    entrySelector: entrySelectorDef,
    ...rest
  } = label;

  void _panelText;
  void _orientation;
  void rest;
  void entrySelectorDef;

  const navigate = useNavigate();
  const entryId = useScopedGeneratorStore(s => s.entryInfo?.lbeid);
  const lbid = useScopedGeneratorStore(s => s.entryInfo?.lbid);
  const setDirty = useScopedGeneratorStore(s => s.setDirty);
  const entries = useSelector(state => state.generatorSlice?.entries);
  const isLatest = entries?.[0]?.value === entryId;
  const isOldest = entries?.[entries.length - 1]?.value === entryId;

  const isReadOnly =
    useScopedGeneratorStore(s => s.entryData?.isReadonly) ?? false;

  const getPrevNextEntry = useCallback(
    (direction: "next" | "prev") => {
      if (!entryId || !entries) return null;
      const entryIds = entries.map(e => e.value);
      const currentIndex = entryIds.findIndex(id => id === entryId);
      if (currentIndex === -1) return null;
      const nextIndex =
        direction === "prev"
          ? currentIndex + 1 < entryIds.length
            ? currentIndex + 1
            : currentIndex
          : currentIndex - 1 >= 0
            ? currentIndex - 1
            : currentIndex;

      if (nextIndex !== currentIndex)
        navigate(`/logbook/${lbid}/lbeid/${entryIds[nextIndex]}`, {
          replace: true,
        });
    },
    [entryId, lbid, entries, navigate],
  );

  if (!text) return null;
  const inlineStyle: CSSProperties = {
    ...(textAlign && {
      justifyContent:
        textAlign === "left"
          ? "flex-start"
          : textAlign === "right"
            ? "flex-end"
            : textAlign === "center"
              ? "center"
              : "flex-start",
    }),
    ...(color && { color }),
    ...(backColor && { backgroundColor: backColor }),
    ...(outline && { border: outline }),
    ...(padding !== undefined ? { padding } : {}),
  };

  const hasDatePickerSelector =
    entrySelectorDef === "datePicker" &&
    Boolean(entrySelector?.id) &&
    Boolean(entrySelector?.onChange);

  const cls = classNames(
    styles.groupLabel,
    labelStyle === "solid" && styles.labelSolid,
    labelStyle === "subtle" && styles.labelSubtle,
    (labelStyle === "clear" || labelStyle === "none") && styles.labelClear,
    bold && styles.bold,
    italic && styles.italic,
    underline && styles.underline,
    entrySelector && styles.withEntrySelector,
  );

  return (
    <div className={cls} style={inlineStyle}>
      <div
        className={classNames(
          styles.labelContainer,
          description && styles.withDescription,
        )}>
        <span>{text}</span>
        {description && (
          <span className={styles.labelDescription}>{description}</span>
        )}
      </div>
      {hasDatePickerSelector && (
        <div
          style={{ display: "flex", alignItems: "center", gap: "8px" }}
          className={styles.entrySelector}>
          {!isReadOnly && (
            <ActionIcon
              className={styles.entrySelectorButton}
              variant="transparent"
              size="sm"
              disabled={isOldest}
              onClick={() => getPrevNextEntry("prev")}>
              <IconChevronLeft size={18} />
            </ActionIcon>
          )}
          <DateInput
            id={entrySelector?.id ?? ""}
            forwardDate={false}
            readonly={isReadOnly}
            value={entrySelector?.value ?? ""}
            onChange={date => {
              if (date) {
                entrySelector?.onChange?.(dayjs(date).format("YYYY-MM-DD"));
              }
            }}
          />
          {!isReadOnly && (
            <ActionIcon
              className={styles.entrySelectorButton}
              variant="transparent"
              size="sm"
              disabled={isLatest}
              onClick={() => getPrevNextEntry("next")}>
              <IconChevronRight size={18} />
            </ActionIcon>
          )}
        </div>
      )}
    </div>
  );
}

// ─── Group ────────────────────────────────────────────────────────────────────

export interface GroupProps {
  group: V2Group;
}

/**
 * Render a layout group with optional label and child nodes.
 * @param group - Group definition to render
 * @returns JSX element
 */
export function Group({ group }: GroupProps) {
  const [dragRowIndex, setDragRowIndex] = useState<number | null>(null);

  const activeSection = usePanelNavigationStore(s => s.activeSection);
  const panelEntries = useScopedGeneratorStore(s => s.panelEntries);
  const addRow = useScopedGeneratorStore(s => s.addRow);
  const copyRow = useScopedGeneratorStore(s => s.copyRow);
  const deleteRow = useScopedGeneratorStore(s => s.deleteRow);
  const moveRow = useScopedGeneratorStore(s => s.moveRow);

  // ── Check if this group should be hidden by accordion toggle ───────────────
  const hasPanel = Boolean(group.panel);
  const isActive = hasPanel && activeSection === group.panel;

  // Build panel map for hierarchy traversal
  const panelMap = new Map(panelEntries?.map(e => [e.panelId, e.parent]));

  const isDescendant =
    hasPanel &&
    activeSection &&
    group.panel &&
    (() => {
      let currentPanel: string | undefined = group.panel;
      while (currentPanel) {
        const parent = panelMap.get(currentPanel);
        if (parent === activeSection) return true;
        currentPanel = parent;
      }
      return false;
    })();

  const isAncestor =
    hasPanel &&
    activeSection &&
    group.panel &&
    (() => {
      let currentPanel: string | null | undefined = activeSection;
      while (currentPanel) {
        const parent = panelMap.get(currentPanel);
        if (parent === group.panel) return true;
        currentPanel = parent;
      }
      return false;
    })();

  const isHidden =
    hasPanel &&
    activeSection !== null &&
    !isActive &&
    !isDescendant &&
    !isAncestor;

  const outerStyle: CSSProperties = {
    ...resolveGroupFlex(group.width),
    ...(group.backColor && { backgroundColor: group.backColor }),
    ...(group.rounded && { borderRadius: group.rounded }),
    ...(group.marginTop && { marginTop: group.marginTop }),
    ...(group.marginBottom && { marginBottom: group.marginBottom }),
    ...(group.minHeight && { minHeight: group.minHeight }),
    ...(group.height && { height: group.height }),
    ...(group.padding && { padding: group.padding }),
  };

  const layout = group.layout ?? "vertical";
  const groupStyleVariant = group.style;

  const label = group.label;
  const labelOrientation = label?.orientation ?? "top";
  const hasLabel = Boolean(label?.text) && labelOrientation !== "none";
  const showTop =
    hasLabel && (labelOrientation === "top" || labelOrientation === undefined);
  const showBottom = hasLabel && labelOrientation === "bottom";
  const entrySelectorId = `entry-selector-${group.panel ?? group.group}`;
  const dispatch = useDispatch();
  const saveDate = useSelector(s => s.generatorSlice.entryDataToSave.entrydate);
  const sortDate = useScopedGeneratorStore(s => s.entryInfo?.sortdate);
  const entryDateLT = useScopedGeneratorStore(s => s.entryInfo?.entryDate);
  const entryDate = saveDate ?? sortDate ?? entryDateLT ?? new Date();

  const entrySelector =
    label?.entrySelector === "datePicker"
      ? {
          id: entrySelectorId,
          value: entryDate ? dayjs(entryDate).format("YYYY-MM-DD") : "",
          onChange: (value: string) => {
            if (!value) return;
            const newDate = dayjs(value).format("DD MMM YYYY");
            dispatch(setEntryDate(newDate));
            markEditing();
            setDirty(true);
          },
        }
      : undefined;

  const targetGroupId = group.id ?? group.group;

  const isRowGroup = [
    group.addRow,
    group.copyRow,
    group.moveRow,
    group.deleteRow,
  ].some(Boolean);
  const content = useMemo(() => group.content ?? [], [group.content]);
  const rows = useMemo(
    () => (isRowGroup ? content.filter(isV2Group) : []),
    [isRowGroup, content],
  );

  function rowsFromContent(nodes: V2ContentNode[]) {
    return nodes.filter(isV2Group);
  }

  const hasRowTemplateFn = useCallback(
    (fn: string) =>
      rowsFromContent(content).some(row =>
        isV2Group(row)
          ? (row.content ?? []).some(child => {
              if (isV2Group(child)) return false;
              const item = child;
              return item.component === "button" && item.function === fn;
            })
          : false,
      ),
    [content],
  );

  const hasRowMoveFn = hasRowTemplateFn("moveRow");

  const template = useMemo(() => {
    if (isRowGroup && content.length > 0) {
      // Find the first group child to act as a template
      const groups = content.filter(isV2Group);
      if (groups.length > 0) {
        // Strip any row-specific values when using it as a template
        const tmpl = JSON.parse(JSON.stringify(groups[0])) as V2Group;
        return tmpl;
      }
    }
    return null;
  }, [isRowGroup, content]);

  useEffect(() => {
    if (!isRowGroup) return;

    const explicitRows = content.filter(isV2Group);
    if (explicitRows.length === 0 && content.length > 0) {
      // Initial setup: Create first row via state action so it's immutable
      const initRowTemplate = {
        ...group,
        content,
        rowInstance: true,
        addRow: false,
        copyRow: false,
        moveRow: false,
        deleteRow: false,
      };
      addRow(targetGroupId, initRowTemplate);
    }
  }, [isRowGroup, content, targetGroupId, addRow, group]);

  const handleAddRow = useCallback(() => {
    if (!template) return;
    addRow(targetGroupId, template);
  }, [targetGroupId, template, addRow]);

  const handleCopyLastRow = useCallback(() => {
    if (rows.length === 0) return;
    copyRow(targetGroupId, rows.length - 1);
  }, [targetGroupId, rows, copyRow]);

  const handleDeleteRow = useCallback(
    (rowIdx: number) => {
      deleteRow(targetGroupId, rowIdx);
    },
    [targetGroupId, deleteRow],
  );

  const handleMoveRow = useCallback(
    (rowIdx: number) => {
      moveRow(targetGroupId, rowIdx, rowIdx + 1);
    },
    [targetGroupId, moveRow],
  );

  const handleRowDrop = useCallback(
    (targetIdx: number) => {
      if (dragRowIndex === null || dragRowIndex === targetIdx) return;
      moveRow(targetGroupId, dragRowIndex, targetIdx);
      setDragRowIndex(null);
    },
    [dragRowIndex, targetGroupId, moveRow],
  );

  const flattenItems = useCallback((node: V2ContentNode): V2Item[] => {
    const items: V2Item[] = [];
    if (isV2Group(node)) {
      (node.content ?? []).forEach(child => {
        items.push(...flattenItems(child));
      });
    } else {
      items.push(node);
    }
    return items;
  }, []);

  const lastRowAction = useScopedGeneratorStore(s => s.lastRowAction);

  useEffect(() => {
    if (!lastRowAction || !isRowGroup) return;

    const { action } = lastRowAction;

    for (let r = 0; r < rows.length; r++) {
      const items = flattenItems(rows[r]);
      if (items.some(item => (item.id ?? item.item) === action.itemId)) {
        if (action.type === "deleteRow") {
          handleDeleteRow(r);
        } else if (action.type === "moveRow") {
          handleMoveRow(r);
        } else if (action.type === "copyRow") {
          handleCopyLastRow();
        } else if (action.type === "addRow") {
          handleAddRow();
        }
        break;
      }
    }
  }, [
    lastRowAction,
    isRowGroup,
    flattenItems,
    rows,
    handleDeleteRow,
    handleMoveRow,
    handleCopyLastRow,
    handleAddRow,
  ]);

  function getRowKey(r: V2ContentNode, ri: number) {
    if (isV2Group(r)) return r.id ?? r.group ?? `row-${ri}`;
    return `${targetGroupId}-row-${ri}`;
  }

  const contentCls = classNames(
    styles.content,
    isRowGroup
      ? styles.vertical
      : layout === "horizontal"
        ? styles.horizontal
        : styles.vertical,
  );

  const containerCls = classNames(
    styles.group,
    (groupStyleVariant ?? "solid") === "solid" && styles.styleSolid,
    groupStyleVariant === "clear" && styles.styleClear,
  );

  if (isHidden) {
    return null;
  }
  return (
    <div id={group.panel} className={containerCls} style={outerStyle}>
      {showTop && label && (
        <GroupLabel label={label} entrySelector={entrySelector} />
      )}

      <div className={contentCls}>
        {isRowGroup
          ? rows.map((r: V2ContentNode, ri: number) => (
              <div
                key={getRowKey(r, ri)}
                className={classNames(styles.rowWithControls, {
                  [styles.dragging]: dragRowIndex === ri,
                })}
                draggable={hasRowMoveFn}
                onDragStart={() => setDragRowIndex(ri)}
                onDragOver={e => {
                  e.preventDefault();
                }}
                onDrop={() => handleRowDrop(ri)}>
                <Group group={r as V2Group} />
              </div>
            ))
          : content.map((child: V2ContentNode, i: number) =>
              isV2Group(child) ? (
                <Group
                  key={child.id ?? child.group ?? `${group.group}-group-${i}`}
                  group={child}
                />
              ) : (
                <Item
                  key={child.id ?? child.item ?? `${group.group}-item-${i}`}
                  item={child}
                />
              ),
            )}
      </div>

      {isRowGroup && [group.addRow, group.copyRow].some(Boolean) && (
        <div className={styles.controls}>
          {[group.addRow].some(Boolean) && (
            <Button
              id={`addRow-${group.group}`}
              label="Add"
              functionType="addRow"
              onClick={handleAddRow}
            />
          )}
          {[group.copyRow].some(Boolean) && (
            <Button
              id={`copyRow-${group.group}`}
              label="Copy"
              functionType="copyRow"
              onClick={handleCopyLastRow}
            />
          )}
        </div>
      )}

      {showBottom && label && (
        <GroupLabel label={label} entrySelector={entrySelector} />
      )}
    </div>
  );
}
